import Shader from './Shader/Shader';
import Texture, { TextureLocation } from './Texture';
import ImageWrapper from './ImageWrapper'
import { IS_SERVER } from '@af-app/config';

type WebGLShaderType = WebGLRenderingContextBase["VERTEX_SHADER"] | WebGLRenderingContextBase["FRAGMENT_SHADER"];

export default class Program {
	private gl:WebGLRenderingContext | WebGL2RenderingContext;

	public shader:Shader;
	public vert:WebGLShader;
	public frag:WebGLShader;
	public core:WebGLProgram;
	public textures:{[name:string]:Texture} = {};
	public images:{[name:string]:TexImageSource} = {};
	private locations:{[key:string]:WebGLUniformLocation} = {};
	private attributes:{[key:string]:GLint} = {};
	
	constructor(gl:WebGLRenderingContext | WebGL2RenderingContext, shader:Shader) {
		this.gl = gl;
		this.core = gl.createProgram();
		this.shader = shader;

		this.vert = this.createShader(gl.VERTEX_SHADER, shader.vert());
		this.frag = this.createShader(gl.FRAGMENT_SHADER, shader.frag());

		gl.attachShader(this.core, this.vert);
		gl.attachShader(this.core, this.frag);

		gl.linkProgram(this.core);
		gl.useProgram(this.core);

		this.createTextures(this.shader.getTextures());

		if (!gl.getProgramParameter(this.core, gl.LINK_STATUS)){
			gl.deleteProgram(this.core);
		}
	}

	public setTexture = (name:string, image:TexImageSource) => {
		let gl = this.gl;
		let texture = this.textures[name];

		this.bindTexture(name, texture);

		if(image != this.images["u_image"]) {
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
			this.images[name] = image;
		}
	}

	public bindTexture = (name:string, texture:WebGLTexture) => {
		let gl = this.gl;
		let textureRef = this.textures[name];
		gl.activeTexture(gl.TEXTURE0 + textureRef.index);
		gl.bindTexture(gl.TEXTURE_2D, texture);
	}

	private createTextures = (textures:string[]) => {
		let gl = this.gl;
		for(let i = 0; i < textures.length; i++){
			let index = i + 1;
			if(!this.isTextureLocation(index)) break;
			let texture = gl.createTexture() as Texture;
			texture.index = index;
			texture.name = textures[i];
			
			gl.bindTexture(gl.TEXTURE_2D, texture);
			if(IS_SERVER){
				gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
				gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
			} else {
				gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
				gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
			}

			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

			this.textures[texture.name] = texture;
		}
	}

	private createShader = (type:WebGLShaderType, source:string) => {
		let shader = this.gl.createShader(type);
		this.gl.shaderSource(shader, source);
		this.gl.compileShader(shader);
		if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
			this.gl.deleteShader(shader);
		}
		return shader;
	}

	private isTextureLocation(location:number):location is TextureLocation {
		return [0,1,2,3,4,5,6,7].indexOf(location) !== -1;
	}

	public getUniformLocation(gl:WebGLRenderingContext | WebGL2RenderingContext, key:string) {
		if(!this.locations[key]) {
			this.locations[key] = gl.getUniformLocation(this.core, key);
		}

		return this.locations[key];
	}

	public getAttribLocation(gl:WebGLRenderingContext | WebGL2RenderingContext, key:string) {
		if(!this.attributes[key]) {
			this.attributes[key] = gl.getAttribLocation(this.core, key);
		}

		return this.attributes[key];
	}

	public delete = () => {
		for(let texture in this.textures){
			if(!this.textures.hasOwnProperty(texture) || !this.gl.isTexture(this.textures[texture])) continue;
			this.gl.deleteTexture(this.textures[texture]);
		}

		for(let image in this.images){
			if(!this.images.hasOwnProperty(image)) continue;
			delete this.images[image];
		}

		if(this.gl.isShader(this.vert)) {
			this.gl.deleteShader(this.vert);
		}

		if(this.gl.isShader(this.frag)) {
			this.gl.deleteShader(this.frag);
		}

		if(this.gl.isProgram(this.core)) {
			this.gl.deleteProgram(this.core);
		}

		delete this.vert;
		delete this.frag;
		delete this.core;
	}
}